import React, { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import { Error404 } from '@/components/custom/error';
import usePermission from '@/hooks/usePermission';
import CourseAdminCard from './course-admin-card';
import { Link, useNavigate } from 'react-router-dom';
import { useCourse } from '@/hooks/useCourse';
import { useClassroom } from '@/hooks/useClassroom';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import { t } from 'i18next';
import { CourseAdminAboutModalClassroom } from '../../course/course-about/course-admin-about-modal-classroom';
import { CourseAdminAboutModalModule } from '../../course/course-about/course-admin-about-modal-module';
import { Button } from '@/components/ui/button';
import { DashboardContent } from '@/layouts/dashboard';
import { Filter, FilterActions, FilterContent, FilterInput } from '@/components/custom/filter';
import { CourseCardAdminWrapper } from '@/components/custom/course';
import usePlayfulTeacher from '@/hooks/usePlayfulTeacher';
import { PlayfulCardButton } from '@/components/custom/playful-card';
import { TiaBanner } from '@/components/custom/tia-banner';
import { CURSOS } from '@/pages/dashboard/home-teacher/teacher-character';

export function CourseAdmin(props) {
  const { uri } = props;

  const navigate = useNavigate();

  const filters = useFormik({
    initialValues: {
      searchTerm: ''
    }
  });

  const { detailedCourses, loading, getDetailedCourses } = useCourse();
  const { detailedClassrooms: classrooms, getDetailedClassrooms, classroomLoading, getCourseModules, courseModules, modulesLoading } = useClassroom();
  const [filteredClassrooms, setFilteredClassrooms] = useState([]);
  const [showModuleModal, setShowModuleModal] = useState(false);
  const [showClassroomModal, setShowClassroomModal] = useState(false);
  const [activeCourse, setActiveCourse] = useState();
  const [filteredCourses, setFilteredCourses] = useState();

  const viewCourse = usePermission('Visualizar lista de cursos', 'Cursos');
  const playfulTeacher = usePlayfulTeacher();

  const handleOpenClassroomModal = course => {
    getDetailedClassrooms(course.id);
    setActiveCourse(course);
    setShowClassroomModal(true);
  };

  const handleOpenModulesModal = async course => {
    await getCourseModules(course.id);
    if (!modulesLoading) {
      setActiveCourse(course);
      setShowModuleModal(true);
    }
  };

  useEffect(() => {
    getDetailedCourses();
  }, []);

  useEffect(() => {
    if (classrooms) {
      setFilteredClassrooms(classrooms);
    }
  }, [classrooms]);

  useEffect(() => {
    if (detailedCourses.length > 0) {
      const filteredCourses = detailedCourses.filter(item => {
        const searchTermFilter = filters.values.searchTerm === '' ? true : item.title !== null && item.title.toLowerCase().includes(filters.values.searchTerm.toLowerCase());
        return searchTermFilter;
      });
      setFilteredCourses(filteredCourses);
    }
  }, [filters.values, detailedCourses]);

  const breadcrumbs = {
    title: 'Cursos e Aulas',
    nav: [
      {
        route: uri,
        name: 'Cursos',
        isActive: true
      }
    ]
  };

  if (!viewCourse) return <Error404 />;

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      {playfulTeacher && (
        <div className="relative mb-6 sm:mt-16">
          <TiaBanner
            className="h-full"
            image={CURSOS}
            imageAlt="Personagem do professor"
            imageHeight={190}
            imageOffset={280}
            bubbleCorner="tl"
            bubbleMaxWidth={600}
            text="Aqui ficam seus cursos! 📚 Acompanhe as turmas matriculadas e avalie o desempenho. Vamos deixar tudo em dia?"
          />
        </div>
      )}

      <Filter>
        <FilterContent>
          <FilterInput
            label={t('textsCommon.research')}
            placeholder={t('filter.courseName')}
            type="search"
            name="search"
            value={filters.values.searchTerm}
            onChange={e => filters.setFieldValue('searchTerm', e.target.value)}
          />
        </FilterContent>

        {usePermission('Criar cursos', 'Cursos') && (
          <FilterActions>
            {playfulTeacher ? (
              <PlayfulCardButton
                className="h-9 w-auto px-6"
                onClick={() => navigate('/curso/criar/sobre')}
              >
                {t('courses.buttonNewCourse')}
              </PlayfulCardButton>
            ) : (
              <Button
                size="sm"
                wide
                asChild
              >
                <Link to={`/curso/criar/sobre`}>{t('courses.buttonNewCourse')}</Link>
              </Button>
            )}
          </FilterActions>
        )}
      </Filter>

      {!filteredCourses && loading && <Loader />}
      {!loading && filteredCourses && filteredCourses?.length === 0 && <EmptyState type="data" />}

      <CourseCardAdminWrapper className={playfulTeacher ? '!gap-4 sm:!grid-cols-2 lg:!grid-cols-3' : undefined}>
        {filteredCourses?.map(item => {
          return (
            <CourseAdminCard
              classroomLoading={classroomLoading}
              setFilteredCourses={setFilteredCourses}
              classrooms={classrooms}
              handleOpenClassroomModal={handleOpenClassroomModal}
              handleOpenModulesModal={handleOpenModulesModal}
              key={item.id}
              course={item}
            />
          );
        })}
      </CourseCardAdminWrapper>

      <CourseAdminAboutModalClassroom
        classroomLoading={classroomLoading}
        classrooms={filteredClassrooms}
        course={activeCourse}
        show={showClassroomModal}
        onClose={() => setShowClassroomModal(false)}
      />

      <CourseAdminAboutModalModule
        courseModules={courseModules}
        course={activeCourse}
        show={showModuleModal}
        onClose={() => setShowModuleModal(false)}
      />
    </DashboardContent>
  );
}
